/**
 * @fileoverview Util functions for the flexible slider scenarios (PRECO-4029).
 * These scenarios are somewhat dynamic:
 * - They start with "bestsellers_", followed by either "category" or "brand"
 * - If "category", then followed by a category level
 * - Then followed by "productBased"
 * - Then followed by either "url" or "history"
 *
 * Examples:
 * - "bestsellers_category_2_productBased_url"
 * - "bestsellers_brand_productBased_history"
 */

import { FlexibleScenario, Scenario } from "@reco/shared/src/constants";
import { type FlexibleScenarioConfig, type SourceType, TargetType } from "../types/records";

export const isValidTargetType = (type: string): type is TargetType => {
  return Object.values(TargetType).includes(type as TargetType);
};

/**
 * Parses a flexible scenario data-type string into a structured configuration object.
 * Expected format examples: "bestsellers_category_2_productBased_url" or "bestsellers_brand_productBased_history"
 * @param dataType The data-type string to parse
 * @returns Parsed configuration object
 * @throws Error if the data-type string is malformed
 */
export function parseFlexibleScenario(dataType: string): FlexibleScenarioConfig {
  const parts = dataType.split("_");

  if (parts.length < 4) {
    throw new Error(`Malformed flexible scenario data-type: ${dataType}`);
  }

  const [baseScenario, targetType, ...remaining] = parts;

  if (baseScenario !== Scenario.BESTSELLERS) {
    throw new Error(`Unsupported base scenario: ${baseScenario}`);
  }

  if (!isValidTargetType(targetType)) {
    throw new Error(`Unsupported target type: ${targetType}`);
  }

  let categoryLevel: number | undefined;
  let sourceIndex: number;

  if (targetType === TargetType.CATEGORY) {
    if (remaining.length < 3) {
      throw new Error(`Malformed category scenario data-type: ${dataType}`);
    }

    const levelStr = remaining[0];
    categoryLevel = parseInt(levelStr, 10);

    if (isNaN(categoryLevel) || categoryLevel < 1) {
      throw new Error(`Invalid category level: ${levelStr}`);
    }

    sourceIndex = 2; // skip level and "productBased"
  } else {
    sourceIndex = 1; // skip "productBased"
  }

  const productBasedIndex = sourceIndex - 1;
  if (remaining[productBasedIndex] !== "productBased") {
    throw new Error(`Expected "productBased" in data-type: ${dataType}`);
  }

  const source = remaining[sourceIndex];
  if (source !== "url" && source !== "history") {
    throw new Error(`Unsupported source type: ${source}`);
  }

  return {
    baseScenario: Scenario.BESTSELLERS,
    targetType: targetType as TargetType,
    categoryLevel,
    source: source as SourceType,
  };
}

/**
 * Checks if a scenario string represents a flexible scenario
 * @param dataType The data-type string to check
 * @returns True if the data-type is a flexible scenario
 */
export const isFlexibleScenario = (dataType: string): dataType is FlexibleScenario =>
  (Object.values(FlexibleScenario) as string[]).includes(dataType);

/**
 * Maps a flexible scenario configuration to the appropriate existing scenario enum
 * Used for backwards compatibility while the new flexible endpoint is being developed
 * @param config Parsed flexible scenario configuration
 * @returns The appropriate existing scenario to use for API calls
 */
export function mapFlexibleToExistingScenario(config: FlexibleScenarioConfig): Scenario {
  if (config.targetType === "category") {
    return Scenario.BESTSELLERS;
  } else {
    return Scenario.BRANDS;
  }
}
